import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import css from './SearchPage.module.css';
import { Icons } from '../../components';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { useConfiguration } from '../../context/configurationContext';
import { isArrayLength } from '../../util/genericHelpers';

const SearchCategorySection = props => {
  const { sliderSettings, viewport } = props;
  const isMobileLayout = viewport?.width && viewport?.width > 0 && viewport?.width < 768;
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();

  const listingFields = config.listing?.listingFields || [];
  const sizesOptions = isArrayLength(listingFields)
    ? listingFields.find(field => field.key === 'size')?.enumOptions || []
    : [];

  const initialDisplayCount = 8;

  // Parse "UK X" or "UK X.5" to a numeric value (e.g., "UK 6.5" -> 6.5)
  const parseUkSize = label => {
    if (!label) return null;
    const m = String(label).trim().match(/^UK\s*(\d+(?:\.\d)?)$/i);
    return m ? Number(m[1]) : null;
  };

  // Normalize & restrict sizes to UK 6..UK 13 (adult page)
  const adultSizeItemsSorted = useMemo(() => {
    const items = (sizesOptions || [])
      .map(opt => {
        const n = parseUkSize(opt.label);
        return n != null
          ? { key: opt.option, name: opt.label, n } // keep numeric for sorting
          : null;
      })
      .filter(Boolean)
      .filter(it => it.n >= 6 && it.n <= 13)
      .sort((a, b) => a.n - b.n);

    return items;
  }, [sizesOptions]);

  // Build the preferred top 8: UK 6, 6.5, 7, 7.5, 8, 8.5, 9, 9
  // If any preferred sizes are missing in data, we backfill from remaining ascending sizes
  const displayedItems = useMemo(() => {
    if (isExpanded) return adultSizeItemsSorted;

    const byNumber = new Map(adultSizeItemsSorted.map(it => [it.n, it]));
    const preferred = [6, 6.5, 7, 7.5, 8, 8.5, 9, 9]; // per spec

    const top = [];
    const used = new Set();

    // Try to place preferred ones first (no blanks)
    preferred.forEach(p => {
      const item = byNumber.get(p);
      if (item && !used.has(item.n)) {
        top.push(item);
        used.add(item.n);
      }
    });

    // Backfill to ensure we always have 8 tiles (fills previous “blank spaces”)
    if (top.length < initialDisplayCount) {
      for (const it of adultSizeItemsSorted) {
        if (top.length >= initialDisplayCount) break;
        if (!used.has(it.n)) {
          top.push(it);
          used.add(it.n);
        }
      }
    }

    return top.slice(0, initialDisplayCount);
  }, [adultSizeItemsSorted, isExpanded]);

  const handleToggle = () => setIsExpanded(prev => !prev);

  // Build query for sizes using pub_size
  const buildSearchParams = sizeKeys => ({ pub_size: `has_any:${sizeKeys.join(',')}` });

  const handleItemClick = sizeKey => {
    const newSelected = selectedSizes.includes(sizeKey)
      ? selectedSizes.filter(s => s !== sizeKey)
      : [...selectedSizes, sizeKey];

    setSelectedSizes(newSelected);

    const params = newSelected.length > 0 ? buildSearchParams(newSelected) : {};
    const searchPath = createResourceLocatorString('SearchPage', routeConfiguration, {}, params);
    history.push(searchPath);
  };

  if (!isArrayLength(adultSizeItemsSorted)) return null;

  const renderItem = item => {
    const isSelected = selectedSizes.includes(item.key);
    return (
      <div
        key={item.key}
        className={`${css.categoryInfo} ${isSelected ? css.selected : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => handleItemClick(item.key)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') handleItemClick(item.key);
        }}
      >
        <span>{item.name}</span>
        {!isMobileLayout && <Icons name="rightSliderArrow" />}
      </div>
    );
  };

  return (
    <div className={css.searchCatagorySec}>
      <h1>
        <FormattedMessage id="SearchCategorySection.title" />
      </h1>

      {isMobileLayout ? (
        <div className={css.mobileCategoriesScroll}>
          <div className={css.scrollContainer}>{adultSizeItemsSorted.map(renderItem)}</div>
        </div>
      ) : (
        <>
          <div className={css.topCategories}>{displayedItems.map(renderItem)}</div>

          {adultSizeItemsSorted.length > initialDisplayCount && (
            <div
              className={css.viewAllCategories}
              role="button"
              tabIndex={0}
              onClick={handleToggle}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleToggle();
              }}
            >
              <FormattedMessage
                id={
                  isExpanded ? 'SearchCategorySection.viewLess' : 'SearchCategorySection.viewMore'
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchCategorySection;
